import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { navigationRef } from '../../navigation/navigationRef';
import { promptDue, recordVisit, savePromptState, type PromptState } from '../../services/feedback/prompt';
import { FeedbackContext, type FeedbackOptions } from './FeedbackContext';
import FeedbackForm from './FeedbackForm';
import useStoreReview from './useStoreReview';

export default function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const accountId = useSelector((state: RootState) => state.session?.account?.account_id);
  const cognitoId = useSelector((state: RootState) => state.authentication?.user?.cognito_id);
  const authorized = useSelector((state: RootState) => state.session?.authorized);
  const locked = useSelector((state: RootState) => state.session?.locked?.show);
  const composerOpen = useSelector((state: RootState) => state.composer?.open);
  const [form, setForm] = useState<(FeedbackOptions & { screen: string; scope: string }) | null>(null);
  const [promptVisible, setPromptVisible] = useState(false);
  const prompt = useRef<PromptState | null>(null);
  const shown = useRef(false);
  const scope = `${accountId}:${cognitoId}:${!!locked}`;
  const [promptScope, setPromptScope] = useState('');
  const available = !!authorized && !!accountId && !!cognitoId && !locked;
  useStoreReview(String(cognitoId || ''), scope, available, !!form || (shown.current && promptVisible) || !!composerOpen);
  useEffect(() => {
    setForm(null);
    setPromptVisible(false);
    prompt.current = null;
    shown.current = false;
    if (!available) return;
    let active = true;
    void recordVisit(scope).then(state => {
      if (!active) return;
      prompt.current = state;
      setPromptScope(scope);
      setPromptVisible(!!state && promptDue(state));
    });
    return () => { active = false; };
  }, [scope, available]);
  const remember = useCallback((forever = false) => {
    if (!prompt.current) return;
    const next = { ...prompt.current, lastPrompt: Date.now(), disabled: forever || prompt.current.disabled };
    prompt.current = next;
    void savePromptState(scope, next).catch(() => setPromptVisible(false));
  }, [scope]);
  const open = useCallback((options: FeedbackOptions = {}) => {
    if (!available) return;
    setForm({ ...options, scope, screen: navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name || '' : '' });
  }, [available, scope]);
  const dismissPrompt = useCallback((forever = false) => { setPromptVisible(false); remember(forever); }, [remember]);
  const promptShown = useCallback(() => {
    if (shown.current) return;
    shown.current = true;
    remember();
  }, [remember]);
  return <FeedbackContext.Provider value={{ available, open, promptVisible: promptScope === scope && promptVisible && available, dismissPrompt, promptShown }}>
    {children}
    {form && form.scope === scope && available && <FeedbackForm key={scope} accountId={String(accountId)} cognitoId={String(cognitoId)} screen={form.screen} initial={form} onClose={() => setForm(null)} onSent={() => dismissPrompt()} />}
  </FeedbackContext.Provider>;
}
